import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {Offer} from '../../../../model/offer/offer';
import {ProposalTile} from '../../../../model/proposal-tile';
import {MatAccordion, MatExpansionPanel} from '@angular/material/expansion';
import {combineLatest, map, Observable, Subject, timer} from 'rxjs';
import {OfferType} from '../../../../enum/offer-type';
import {getCurrencySymbol} from '@angular/common';
import {QuestionOptions} from '../../../../constants/question-options';
import {OfferStatus} from '../../../../enum/offer-status';
import {Store} from '@ngrx/store';
import {selectUserId} from '../../../../store/user/selectors/user.selectors';
import {OfferOverviewComponent} from '../../../offer-overview/offer-overview.component';

@Component({
    selector: 'app-offers-details-tab',
    templateUrl: './offers-details-tab.component.html',
    styleUrls: ['./offers-details-tab.component.scss'],
    standalone: false
})
export class OffersDetailsTabComponent implements AfterViewInit, OnDestroy {
  @ViewChild(MatAccordion) accordion: MatAccordion;
  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;
  @ViewChild(OfferOverviewComponent) offerOverviewComponent: OfferOverviewComponent;
  @Input() proposalTile: ProposalTile;
  @Input() offer: Offer;
  @Input() offers: Offer[] = [];
  @Output() offerChange = new EventEmitter<Offer>();
  @Output() navigateToOffer = new EventEmitter<Offer>();
  userId$ = this.store.select(selectUserId);

  timer$ = timer(0, 15000).pipe(map(() => {
    const timeDifference = this.offer.deletionTime.getTime() - new Date().getTime();
    const minutes = timeDifference / (60 * 1000);
    return `${Math.floor(minutes / 60)}:${(Math.floor(minutes % 60)).toString().padStart(2, '0')}`;

  }));

  OfferType = OfferType;
  OfferStatus = OfferStatus;
  allExpandState = true;

  protected destroy$ = new Subject<void>();
  QuestionOptions = QuestionOptions;

  constructor(private readonly cd: ChangeDetectorRef, private readonly store: Store) {
  }

  ngAfterViewInit(): void {
    this.clickButton(true);
    this.cd.detectChanges();
    const obs: Observable<boolean>[] = [];
    for (const panel of this.panels) {
      obs.push(panel.expandedChange);
    }
    combineLatest(obs).subscribe((array: boolean[]) => {
      if (array.every(value => value)) {
        this.allExpandState = true;
      } else if (array.every(value => !value)) {
        this.allExpandState = false;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clickButton($event: any): void {
    if ($event) {
      this.accordion.openAll();
    } else {
      this.accordion.closeAll();
    }
  }

  currencySymbol() {
    return getCurrencySymbol(this.offer.currency, 'narrow');
  }

  difference() {
    const timeDifference = this.offer.deletionTime.getTime() - new Date().getTime();
    return Math.round(timeDifference / (3600 * 1000));
  }

  get currentIndex(): number {
    return this.offers.findIndex(o => o.offerId === this.offer?.offerId);
  }

  get hasPrevious(): boolean {
    if (this.offers.length > 0 && this.offers[0].offerType === OfferType.FIXED)
      return this.currentIndex > 1;
    else
      return this.currentIndex > 0;
  }

  get hasNext(): boolean {
    return this.currentIndex < this.offers.length - 1;
  }

  navigatePrevious(): void {
    if (this.hasPrevious) {
      this.navigateToOffer.emit(this.offers[this.currentIndex - 1]);
      this.offerOverviewComponent.markOfferAsRead(this.offers[this.currentIndex - 1]);
    }
  }

  navigateNext(): void {
    if (this.hasNext) {
      this.navigateToOffer.emit(this.offers[this.currentIndex + 1]);
      this.offerOverviewComponent.markOfferAsRead(this.offers[this.currentIndex + 1]);
    }
  }
}
